const STORAGE_KEY = 'gevme-integration-login';
const PENDING_KEY = 'gevme-integration-login-pending';

const FIELDS = [
    'company_id',
    'client_id',
    'redirect_uri',
    'response_type',
    'state',
    'code_challenge',
    'code_challenge_method',
    'resource',
    'scope',
    'iss',
];

const CLIENTS = [
    {
        name: 'Claude',
        domain: 'claude.ai',
        test: (url) => hostIs(url, ['claude.ai', 'claude.com']),
    },
    {
        name: 'ChatGPT',
        domain: 'chatgpt.com',
        test: (url) =>
            hostIs(url, ['chatgpt.com', 'chat.openai.com', 'openai.com']),
    },
    {
        name: 'Cursor',
        domain: 'cursor.com',
        test: (url) => url.protocol === 'cursor:',
    },
    {
        name: 'VS Code',
        domain: 'code.visualstudio.com',
        test: (url) =>
            url.protocol === 'vscode:' || url.protocol === 'vscode-insiders:',
    },
    {
        name: 'Windsurf',
        domain: 'windsurf.com',
        test: (url) => url.protocol === 'windsurf:',
    },
];

// ponytail: icons come from Google's favicon service so any web client gets one.
// Bundle SVGs under public/assets if that host is ever blocked.
function iconFor(domain) {
    return domain
        ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(
              domain
          )}&sz=128`
        : null;
}

function hostIs(url, hosts) {
    return hosts.some(
        (host) => url.hostname === host || url.hostname.endsWith(`.${host}`)
    );
}

export function readAuthorizeSearch(search) {
    const params = new URLSearchParams(search || '');
    const values = {};
    FIELDS.forEach((key) => {
        values[key] = params.get(key) || '';
    });
    if (!values.code_challenge_method) values.code_challenge_method = 'S256';
    return values;
}

export function isIntegrationQuery(values) {
    return Boolean(values?.client_id && values?.redirect_uri);
}

export function encodeIntegrationState(values) {
    return btoa(JSON.stringify(values))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export function decodeIntegrationState(value) {
    if (!value) return null;
    try {
        const pad = value.replace(/-/g, '+').replace(/_/g, '/');
        const data = JSON.parse(atob(pad));
        return isIntegrationQuery(data) ? data : null;
    } catch (error) {
        return null;
    }
}

export function clientFromRedirect(uri) {
    try {
        const url = new URL(uri);
        const found = CLIENTS.find((client) => client.test(url));
        if (found) return { name: found.name, icon: iconFor(found.domain) };
        const web = url.protocol === 'https:' || url.protocol === 'http:';
        return web
            ? { name: url.hostname, icon: iconFor(url.hostname) }
            : { name: 'app', icon: null };
    } catch (error) {
        return { name: 'app', icon: null };
    }
}

export function saveIntegrationLogin(values) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
}

export function loadIntegrationLogin() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
}

export function hasIntegrationLogin() {
    return isIntegrationQuery(loadIntegrationLogin());
}

export function armIntegrationLogin() {
    if (hasIntegrationLogin()) sessionStorage.setItem(PENDING_KEY, '1');
}

export function integrationArmed() {
    return sessionStorage.getItem(PENDING_KEY) === '1' && hasIntegrationLogin();
}

export function dropIdleIntegrationLogin() {
    if (!integrationArmed()) sessionStorage.removeItem(STORAGE_KEY);
}

export function integrationReturnPath() {
    const values = loadIntegrationLogin();
    if (!isIntegrationQuery(values)) return '/login';
    const params = new URLSearchParams();
    FIELDS.forEach((key) => {
        if (values[key]) params.set(key, values[key]);
    });
    return `/authorize?${params.toString()}`;
}

export function rememberGoogleReturn(search) {
    const params = new URLSearchParams(search || '');
    const values = decodeIntegrationState(params.get('state'));
    if (values) saveIntegrationLogin(values);
    return values;
}

function clearIntegrationLogin() {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(PENDING_KEY);
}

export async function completeIntegrationLogin() {
    const params = loadIntegrationLogin();
    if (!params?.company_id || !params.redirect_uri || !params.code_challenge) {
        throw new Error('This connection link is invalid.');
    }
    const response = await fetch(
        `${
            process.env.REACT_APP_MYXP_API
        }/mcp/oauth/companies/${encodeURIComponent(params.company_id)}/grant`,
        {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                redirect_uri: params.redirect_uri,
                code_challenge: params.code_challenge,
                code_challenge_method: params.code_challenge_method || 'S256',
            }),
        }
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(
            body.error_description ||
                body.message ||
                'Could not connect this account.'
        );
    }
    const target = new URL(params.redirect_uri);
    target.searchParams.set('code', body.code);
    if (params.state) target.searchParams.set('state', params.state);
    if (params.iss) target.searchParams.set('iss', params.iss);
    clearIntegrationLogin();
    window.location.assign(target.toString());
}
